import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import apiClient from "../../api/apiClient";
import useChatStore from "../../store/chatStore";
import useAuthStore from "../../store/authStore";

// Secure encryption and key generation helpers for remember-me functionality
const getDeviceKey = () => {
  try {
    let key = "";
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.startsWith("rm_key=")) {
        key = decodeURIComponent(cookie.substring(7));
        break;
      }
    }
    if (!key) {
      key = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
      const secureFlag = window.location.protocol === "https:" ? "Secure;" : "";
      document.cookie = `rm_key=${encodeURIComponent(key)}; path=/; max-age=31536000; SameSite=Strict; ${secureFlag}`;
    }
    return key;
  } catch (e) {
    return "fallback_device_key_static_obfuscate_1984";
  }
};

const encryptData = (text) => {
  if (!text) return "";
  try {
    const key = getDeviceKey();
    const encoder = new TextEncoder();
    const textBytes = encoder.encode(text);
    const resultBytes = new Uint8Array(textBytes.length);
    for (let i = 0; i < textBytes.length; i++) {
      resultBytes[i] = textBytes[i] ^ key.charCodeAt(i % key.length);
    }
    let binString = "";
    for (let i = 0; i < resultBytes.length; i++) {
      binString += String.fromCharCode(resultBytes[i]);
    }
    return btoa(binString);
  } catch (e) {
    return text;
  }
};

const decryptData = (cipherText) => {
  if (!cipherText) return "";
  try {
    const key = getDeviceKey();
    const binString = atob(cipherText);
    const resultBytes = new Uint8Array(binString.length);
    for (let i = 0; i < binString.length; i++) {
      resultBytes[i] = binString.charCodeAt(i) ^ key.charCodeAt(i % key.length);
    }
    const decoder = new TextDecoder();
    return decoder.decode(resultBytes);
  } catch (e) {
    return "";
  }
};

function Login() {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    remember: false,
  });
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    try {
      useChatStore.getState().reset();
    } catch (e) {
      console.warn("Failed to reset chat store on Login mount:", e);
    }
    try {
      const savedUsernameCipher = localStorage.getItem("rm_u");
      const savedPasswordCipher = localStorage.getItem("rm_p");
      if (savedUsernameCipher && savedPasswordCipher) {
        const savedUsername = decryptData(savedUsernameCipher);
        const savedPassword = decryptData(savedPasswordCipher);
        if (savedUsername && savedPassword) {
          setFormData({ username: savedUsername, password: savedPassword, remember: true });
        }
      }
    } catch (e) {
      console.warn("Failed to load remembered credentials from localStorage:", e);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.username || !formData.password) {
      setError("Please enter both username and password.");
      return;
    }
    setError("");

    try {
      const response = await apiClient.post("/accounts/api/login/", {
        username: formData.username,
        password: formData.password,
      });
      const data = response.data;

      try {
        if (formData.remember) {
          localStorage.setItem("rm_u", encryptData(formData.username));
          localStorage.setItem("rm_p", encryptData(formData.password));
        } else {
          localStorage.removeItem("rm_u");
          localStorage.removeItem("rm_p");
        }
      } catch (e) {
        console.warn("Failed to save credentials to localStorage:", e);
      }

      try {
        useChatStore.getState().reset();
      } catch (e) {
        console.warn("Failed to reset chat store on login success:", e);
      }

      useAuthStore.getState().setAuth({
        token: "present",
        role: data.role || null,
        employeeId: data.employee_id || null,
        fullName: data.full_name || "",
        mustChangePassword: data.must_change_password === true || String(data.must_change_password) === "true",
        isSuperuser: data.is_superuser === true || String(data.is_superuser) === "true",
        user: data.employee_id ? {
          employee_id: data.employee_id,
          full_name: data.full_name || "",
          role: data.role
        } : null
      });

      if (data.force_password_reset || data.must_change_password) {
        navigate("/reset-password", { replace: true });
        return;
      }

      // Check if redirect query parameter exists
      const params = new URLSearchParams(window.location.search);
      const redirect = params.get('redirect');
      if (redirect) {
        const roleLower = (data.role || "EMPLOYEE").toLowerCase();
        let target = `/${roleLower}/${redirect}`;
        if (redirect === 'chat') {
          const convId = params.get('conversation_id');
          if (convId) {
            try {
              useChatStore.getState().selectConversation(Number(convId));
            } catch (e) {
              console.warn("Failed to select conversation in Login success redirect:", e);
            }
          }
          target = `/${roleLower}/chat`;
        } else if (redirect === 'pending-leave-list') {
          target = `/${roleLower}/pending-leave-list`;
        } else if (redirect === 'leave-report') {
          target = `/${roleLower}/leave-report`;
        } else if (redirect === 'dashboard') {
          target = `/${roleLower}/dashboard`;
        }
        navigate(target, { replace: true });
        return;
      }

      if (data.role === "ADMIN") navigate("/admin/dashboard", { replace: true });
      else if (data.role === "MANAGER") navigate("/manager/dashboard", { replace: true });
      else if (data.role === "EMPLOYEE") navigate("/employee/dashboard", { replace: true });
      else setError("Unknown role received.");
    } catch (err) {
      console.error("Login error:", err);
      setError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Login failed. Please check credentials."
      );
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-cover bg-center relative px-4 py-8"
      style={{ backgroundImage: "url(/images/facade-bg.png)" }}
    >
      <div className="absolute inset-0 bg-black/45" />

      <div className="relative w-full max-w-[560px] bg-[#dcebff]/95 rounded-[18px] shadow-2xl p-6 sm:p-10 z-10">

        {/* Logo */}
        <div className="text-center mb-5">
          <img src="/images/logo.png" alt="SAHVIA Logo" className="h-10 sm:h-12 mx-auto" />
        </div>

        <h2 className="text-center text-[20px] sm:text-[26px] font-semibold text-[#0f2a44] mb-1">
          Login to your enterprise account
        </h2>
        <p className="text-center text-sm text-[#425b75] mb-6 sm:mb-7">
          Access your enterprise dashboard
        </p>

        {/* Error */}
        {error && (
          <div className="mb-5 text-sm text-[#d8000c] font-semibold bg-[#ffdddd] border-l-4 border-[#d8000c] px-4 py-2 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">

          {/* Username */}
          <div>
            <label className="block text-[13px] font-semibold text-[#0f2a44] mb-1">
              Employee ID / Email
            </label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="employee@sahvia.com"
              autoComplete="username"
              className="w-full h-11 rounded-[10px] px-4 text-sm bg-[#f4f9ff] border border-[#b8cce6]
                focus:outline-none focus:border-[#4a90e2] focus:ring-4 focus:ring-[#4a90e2]/30 transition"
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-[13px] font-semibold text-[#0f2a44] mb-1">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                autoComplete="current-password"
                className="w-full h-11 rounded-[10px] px-4 pr-11 text-sm bg-[#f4f9ff] border border-[#b8cce6]
                  focus:outline-none focus:border-[#4a90e2] focus:ring-4 focus:ring-[#4a90e2]/30 transition"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#425b75] hover:text-[#0f2a44] transition p-1"
                tabIndex={-1}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Remember Me + Forgot */}
          <div className="flex flex-wrap justify-between items-center gap-2 text-sm">
            <label className="flex items-center gap-2 text-[#0f2a44] cursor-pointer select-none">
              <input
                type="checkbox"
                name="remember"
                checked={formData.remember}
                onChange={handleChange}
                className="w-4 h-4 accent-[#4a90e2]"
              />
              Remember me
            </label>
            <Link to="/forgot-password" className="text-[#2a6edc] hover:underline">
              Forgot Password?
            </Link>
          </div>

          {/* Submit */}
          <button
            type="submit"
            className="w-full py-3 rounded-[10px] text-white text-sm font-semibold
              bg-gradient-to-r from-[#4a90e2] to-[#6bb3ff] hover:opacity-95 transition active:scale-[0.98]"
          >
            Sign In
          </button>
        </form>

        <p className="text-center text-[12px] sm:text-[12.5px] text-[#425b75] mt-6 sm:mt-7">
          Secure • Scalable • Built for Enterprise Teams
        </p>

        <div className="text-center mt-2.5">
          <Link
            to="/privacy-policy"
            className="text-[12px] text-[#2a6edc] hover:underline font-medium inline-flex items-center gap-1 transition"
          >
            Privacy Policy
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Login;